import React, { useMemo, useState } from 'react';
import type { Task, TaskAssignment, TaskProjectLink } from '../../../../shared/entities';
import { loadTasksBundle } from '../../tasks/taskDataLoader';
import { hasAnyPermission, hasPermission } from '../../../../shared/permissions';
import { visibleMemberIds, isTaskVisible, taskBelongsToProject } from '../../tasks/taskPermissions';
import { WidgetFrame, WList, type WListRow } from '../../../components/widgets';
import { daysBetween, shortDate, todayIso, useSettledFetch } from './shared';
import type { ProjectWidgetCtx, ProjectWidgetDef } from './context';

// ═══════════════════════════════════════════════════════════════
// ATTENTION (new, T3B) — this project's named, clickable items that
// need a person's action: overdue / due-soon deliverables, overdue
// tasks, and missing setup (no PM, no team, no forma). No `requires` —
// each item only appears when its own source is loaded AND (for
// tasks) the viewer holds a tasks.view_* key. Never a hard error card:
// a source that failed just drops its items.
// ═══════════════════════════════════════════════════════════════

const TASK_VIEW_KEYS = ['tasks.view_own', 'tasks.view_team', 'tasks.view_all'];

interface AttentionItem {
  id: string;
  tone: 'danger' | 'warning';
  primary: string;
  meta: string;
  onClick: () => void;
}

function AttentionBody({ ctx }: { ctx: ProjectWidgetCtx }) {
  const canSeeTasks = hasAnyPermission(ctx.userPermissions, TASK_VIEW_KEYS);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [assignments, setAssignments] = useState<TaskAssignment[]>([]);
  const [links, setLinks] = useState<TaskProjectLink[]>([]);
  const userId = ctx.authUser?.teamMemberId || '';

  // Decision 24: ONE shared loader, same as the Tasks widget and
  // useTasksData — only called at all when the viewer holds a tasks.view_*
  // key (this widget's items quietly drop rather than error otherwise).
  const { status: taskStatus } = useSettledFetch(
    () => (canSeeTasks ? [loadTasksBundle()] : []),
    (results) => {
      const [bR] = results;
      if (bR?.status === 'fulfilled') {
        setTasks(bR.value.tasks);
        setAssignments(bR.value.assignments);
        setLinks(bR.value.links);
      }
    },
    [ctx.project.id, canSeeTasks]
  );

  const today = todayIso();

  const overdueTasks = useMemo(() => {
    if (!canSeeTasks) return [];
    const visIds = visibleMemberIds({
      userId,
      permissions: ctx.userPermissions,
      members: ctx.members,
      teams: ctx.teams,
      teamAssignments: ctx.teamAssignments,
    });
    return tasks.filter(
      (t) =>
        t.status !== 'done' &&
        !!t.dueDate &&
        t.dueDate < today &&
        taskBelongsToProject(t, links, ctx.project.id) &&
        isTaskVisible(t, assignments, userId, visIds)
    );
  }, [
    tasks,
    assignments,
    links,
    userId,
    ctx.userPermissions,
    ctx.members,
    ctx.teams,
    ctx.teamAssignments,
    ctx.project.id,
    today,
    canSeeTasks,
  ]);

  const attentionDeliverables = useMemo(() => {
    return ctx.deliverables.data.filter((d) => {
      if (d.status === 'complete' || !d.dueDate) return false;
      return daysBetween(today, d.dueDate) <= 7;
    });
  }, [ctx.deliverables.data, today]);

  const canSeeFormas = hasPermission(ctx.userPermissions, 'formas.view');

  // Flat ranked list (Widgets R2 Decision B.1): overdue first, then
  // due-soon, then setup nudges (no PM / no team / no forma) — three
  // buckets built separately, then concatenated in that order.
  const items: AttentionItem[] = useMemo(() => {
    const overdueItems: AttentionItem[] = [];
    const dueSoonItems: AttentionItem[] = [];
    for (const d of attentionDeliverables) {
      const days = daysBetween(today, d.dueDate!);
      const item: AttentionItem = {
        id: `deliverable-${d.id}`,
        tone: days < 0 ? 'danger' : 'warning',
        primary: d.name,
        meta:
          days < 0
            ? `${Math.abs(days)}d overdue`
            : days === 0
              ? 'due today'
              : `due ${shortDate(d.dueDate!)}`,
        onClick: () => ctx.onDrill({ kind: 'deliverable', deliverableId: d.id }),
      };
      (days < 0 ? overdueItems : dueSoonItems).push(item);
    }
    for (const t of overdueTasks) {
      overdueItems.push({
        id: `task-${t.id}`,
        tone: 'danger',
        primary: t.title,
        meta: `${Math.abs(daysBetween(today, t.dueDate!))}d overdue`,
        onClick: () => ctx.onDrill({ kind: 'task', taskId: t.id }),
      });
    }
    const nudgeItems: AttentionItem[] = [];
    if (!ctx.project.pmId) {
      nudgeItems.push({
        id: 'no-pm',
        tone: 'warning',
        primary: 'No PM assigned',
        meta: 'set a project manager',
        // Project Canvas R1 — "Details" is a widget now, not the retired
        // ProjectPanel chrome (was 'project-panel').
        onClick: () => ctx.onDrill({ kind: 'widget-focus', widgetId: 'project_details' }),
      });
    }
    if (ctx.assignments.status === 'ready' && ctx.assignments.data.length === 0) {
      nudgeItems.push({
        id: 'no-team',
        tone: 'warning',
        primary: 'No team assigned',
        meta: 'add people to this project',
        onClick: () => ctx.onDrill({ kind: 'widget-focus', widgetId: 'team' }),
      });
    }
    if (canSeeFormas && ctx.formas.status === 'ready' && ctx.formas.data.length === 0) {
      nudgeItems.push({
        id: 'no-forma',
        tone: 'warning',
        primary: 'No forma bound',
        meta: 'pick a formatting template',
        // Forma folded into Specs (Decision 5) — the widget-focus target
        // now points at the Specs widget, which carries the forma strip.
        onClick: () => ctx.onDrill({ kind: 'widget-focus', widgetId: 'specs' }),
      });
    }
    return [...overdueItems, ...dueSoonItems, ...nudgeItems];
  }, [attentionDeliverables, overdueTasks, today, ctx, canSeeFormas]);

  const loading =
    ctx.deliverables.status === 'loading' ||
    ctx.assignments.status === 'loading' ||
    ctx.formas.status === 'loading' ||
    (canSeeTasks && taskStatus === 'loading');

  const frameStatus = loading ? 'loading' : items.length === 0 ? 'empty' : 'ready';

  const rows: WListRow[] = items.map((it) => ({
    id: it.id,
    lead: (
      <span
        aria-hidden="true"
        style={{
          display: 'inline-block',
          width: 'var(--space-2)',
          height: 'var(--space-2)',
          borderRadius: 'var(--radius-full)',
          background: it.tone === 'danger' ? 'var(--color-danger)' : 'var(--color-warning)',
        }}
      />
    ),
    primary: it.primary,
    meta: it.meta,
    onClick: it.onClick,
  }));

  return (
    <WidgetFrame
      title="Attention"
      status={frameStatus}
      form="list"
      empty={{ title: 'Nothing needs you' }}
    >
      {/* Attention's items are heterogeneous (deliverables/tasks/nudges) with
          no single "view all" destination page — capped at max, same as
          today, rather than wiring a View all link to nowhere. */}
      <WList rows={rows} max={Math.max(rows.length, 1)} />
    </WidgetFrame>
  );
}

export const attentionWidget: ProjectWidgetDef = {
  id: 'attention',
  title: 'Attention',
  description:
    'This project’s clickable to-dos: overdue/due-soon deliverables, overdue tasks, missing PM/team/forma.',
  category: 'overview',
  cols: { min: 3, max: 12 },
  // Project Canvas R1 (Decision 2/C) — full width by default so a fresh
  // board's row 2 matches the migration's own `ensureAttentionWidget`
  // insertion (`w: 12`), same as every pre-existing saved board.
  defaultSize: 'expanded',
  required: false,
  inputSurface: false,
  render: (ctx) => <AttentionBody ctx={ctx} />,
};
